import { useEffect } from 'react';
import { QueryClient, useQuery, useQueryClient } from 'react-query';
import { getrevisions } from 'services/revisions';
import { RevisionStatus } from 'utils/contracts';

interface Revision {
  id: string;
  status: string;
  [key: string]: unknown;
}

/** Shared across tabs so a run started on the board updates an open inspection page. */
export const PENDING_LAUNCH_KEY = 'revision-optimistic-pending';

const SERVER_CAUGHT_UP = new Set<string>([
  RevisionStatus.PENDING,
  RevisionStatus.PROCESSING,
  RevisionStatus.VALIDATING,
  RevisionStatus.SUCCESS,
]);

const parsePendingLaunchIds = (raw: string | null): Set<string> => {
  try {
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
  } catch {
    return new Set();
  }
};

const readPendingLaunchIds = (): Set<string> => {
  try {
    return parsePendingLaunchIds(localStorage.getItem(PENDING_LAUNCH_KEY));
  } catch {
    return new Set();
  }
};

const writePendingLaunchIds = (ids: Set<string>) => {
  try {
    if (ids.size === 0) {
      localStorage.removeItem(PENDING_LAUNCH_KEY);
      return;
    }
    localStorage.setItem(PENDING_LAUNCH_KEY, JSON.stringify([...ids]));
  } catch {
    // ignore quota / private mode
  }
};

/**
 * Keep a just-launched revision as pending until the server reports it.
 * Other tabs see the localStorage write and invalidate their revision queries.
 */
export const overlayPendingLaunchStatus = (
  status: string | undefined,
  revisionId: string | undefined
): string | undefined => {
  if (!revisionId) return status;
  const ids = readPendingLaunchIds();
  const key = String(revisionId);
  if (!ids.has(key)) return status;
  if (status && SERVER_CAUGHT_UP.has(status)) {
    ids.delete(key);
    writePendingLaunchIds(ids);
    return status;
  }
  return RevisionStatus.PENDING;
};

const registerPendingLaunch = (revisionId: string) => {
  const ids = readPendingLaunchIds();
  ids.add(revisionId);
  writePendingLaunchIds(ids);
};

export const clearRevisionPendingOptimistic = (revisionId: string | undefined) => {
  if (!revisionId) return;
  const ids = readPendingLaunchIds();
  if (!ids.delete(revisionId)) return;
  writePendingLaunchIds(ids);
};

/**
 * After a refetch, keep launch-in-flight revisions as pending until the
 * server catches up (avoids Open Error → Pending flash on the board).
 */
export const applyOptimisticPendingStatuses = <T extends Revision>(
  revisions: T[] | undefined
): T[] | undefined => {
  if (!Array.isArray(revisions) || revisions.length === 0) return revisions;
  const ids = readPendingLaunchIds();
  if (ids.size === 0) return revisions;

  const next = revisions.map((rev) => {
    if (!rev?.id) return rev;
    const status = overlayPendingLaunchStatus(rev.status, String(rev.id));
    if (status === rev.status) return rev;
    return { ...rev, status: status ?? rev.status };
  });

  return next;
};

/**
 * Upsert pending status in the board list cache + remember the id so
 * a subsequent refetch cannot flash Open Error.
 */
export const markRevisionPendingOptimistic = (
  queryClient: QueryClient,
  projectId: string | undefined,
  revisionId: string | undefined
) => {
  if (!projectId || !revisionId) return;
  registerPendingLaunch(revisionId);
  patchCachedRevisionPending(queryClient, revisionId);

  queryClient.setQueryData(
    ['GetAllRevisions', projectId],
    (old: Revision[] | undefined) => {
      if (!Array.isArray(old) || old.length === 0) return old;
      const idx = old.findIndex((rev) => rev?.id === revisionId);
      if (idx < 0) return old;
      const next = old.slice();
      next[idx] = { ...next[idx], status: RevisionStatus.PENDING };
      return next;
    }
  );
};

export const useRevision = (projectId: string) => {
  return useQuery({
    queryKey: ['GetAllRevisions', projectId],
    queryFn: async () => {
      try {
        const data = await getrevisions(projectId);
        return applyOptimisticPendingStatuses(data);
      } catch (error) {
        console.error('Error when fetching revisions:', error);
        throw error;
      }
    },
    refetchInterval: (data) => {
      if (!data) return 5000;
      const hasNonSuccessItem = data.some(
        (item: Revision) =>
          item.status !== 'success' && item.status !== 'failed'
      );
      return hasNonSuccessItem ? 10000 : false;
    },
    enabled: !!projectId,
  });
};

const patchCachedRevisionPending = (queryClient: QueryClient, revisionId: string) => {
  queryClient.setQueriesData(['GetRevisionByID', revisionId], (old: any) => {
    if (!old?.data) return old;
    return { ...old, data: { ...old.data, status: RevisionStatus.PENDING } };
  });
  queryClient.setQueriesData(
    ['getRevisionByIdWithProject', revisionId],
    (old: any) => {
      if (!old || typeof old !== 'object') return old;
      return { ...old, revision_status: RevisionStatus.PENDING };
    }
  );
};

/**
 * Mount once under QueryClientProvider.
 * A simulation started in another tab writes localStorage; this tab
 * invalidates the revision queries the inspection page is reading.
 */
export const RevisionLaunchCacheSync = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== PENDING_LAUNCH_KEY) return;
      const next = parsePendingLaunchIds(event.newValue);
      const prev = parsePendingLaunchIds(event.oldValue);
      const touched = new Set<string>([...next, ...prev]);
      if (touched.size === 0) return;

      touched.forEach((revisionId) => {
        if (next.has(revisionId)) {
          patchCachedRevisionPending(queryClient, revisionId);
        }
        queryClient.invalidateQueries(['GetRevisionByID', revisionId]);
        queryClient.invalidateQueries(['getRevisionByIdWithProject', revisionId]);
      });
      queryClient.invalidateQueries(['GetAllRevisions']);
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [queryClient]);

  return null;
};
